import { forwardRef } from 'react';

/**
 * Icon-only button (spec §7.1, §12). Every icon-only control needs an
 * accessible name — `label` is required and rendered as `aria-label`. Missing
 * it is a dev-time error, not a silent a11y gap.
 *
 * @param {object} props
 * @param {string} props.label - accessible name, rendered as aria-label. Pass
 *   a translated string (i18n `t(...)`), never a hardcoded literal.
 * @param {'default'|'large'} [props.size='default']
 * @param {'danger'} [props.tone] - matches `[data-tone="danger"]` in buttons.css.
 * @param {boolean} [props.pressed] - toggle state, rendered as aria-pressed.
 * @param {string} [props.className]
 * @param {import('react').ReactNode} props.children - the icon.
 */
export const IconButton = forwardRef(function IconButton(
  { label, size = 'default', tone, pressed, type = 'button', className = '', children, ...rest },
  ref
) {
  if (import.meta.env.DEV && !label) {
    // eslint-disable-next-line no-console
    console.error(
      '[IconButton] missing required `label` prop — every icon-only control needs an accessible name (spec §7.1, §12).'
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={`etch-icon-button ${className}`.trim()}
      aria-label={label}
      data-size={size === 'default' ? undefined : size}
      data-tone={tone}
      aria-pressed={pressed}
      {...rest}
    >
      {children}
    </button>
  );
});

export default IconButton;
